// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/kritaPreset.js
//
// DOM-free Krita preset reading, safe to run in a worker. A .kpp file is a
// PNG thumbnail whose zTXt/iTXt/tEXt chunk holds the preset XML. The XML is
// read with bounded regular expressions only: no DTD or entity expansion.
import { unzlibSync } from 'fflate';

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];
const MAX_PRESET_XML_BYTES = 24 * 1024 * 1024;

export function isPngBytes(bytes) {
  if (!bytes || bytes.length < 8) return false;
  for (let i = 0; i < 8; i += 1) if (bytes[i] !== PNG_SIGNATURE[i]) return false;
  return true;
}

function readU32Be(bytes, offset) {
  return ((bytes[offset] << 24) >>> 0) + (bytes[offset + 1] << 16) + (bytes[offset + 2] << 8) + bytes[offset + 3];
}

function latin1(bytes) {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) out += String.fromCharCode(bytes[i]);
  return out;
}

function utf8(bytes) {
  return typeof TextDecoder !== 'undefined' ? new TextDecoder('utf-8').decode(bytes) : latin1(bytes);
}

// Inflate with a hard output cap so a hostile zTXt chunk cannot balloon.
function inflateCapped(payload) {
  const out = new Uint8Array(Math.min(MAX_PRESET_XML_BYTES, Math.max(64 * 1024, payload.length * 64)));
  try {
    const result = unzlibSync(payload, { out });
    return utf8(result);
  } catch {
    return '';
  }
}

export function readPngTextChunks(bytes) {
  const chunks = [];
  if (!isPngBytes(bytes)) return chunks;
  let offset = 8;
  while (offset + 12 <= bytes.length) {
    const length = readU32Be(bytes, offset);
    const type = String.fromCharCode(bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7]);
    const start = offset + 8;
    const end = start + length;
    if (end + 4 > bytes.length) break;
    if (type === 'tEXt' || type === 'zTXt' || type === 'iTXt') {
      const data = bytes.subarray(start, end);
      const nul = data.indexOf(0);
      if (nul > 0) {
        const keyword = latin1(data.subarray(0, nul));
        let text = '';
        if (type === 'tEXt') {
          text = latin1(data.subarray(nul + 1));
        } else if (type === 'zTXt') {
          text = data[nul + 1] === 0 ? inflateCapped(data.subarray(nul + 2)) : '';
        } else {
          const compressed = data[nul + 1] === 1;
          let cursor = nul + 3;
          const langEnd = data.indexOf(0, cursor);
          if (langEnd >= 0) {
            const transEnd = data.indexOf(0, langEnd + 1);
            if (transEnd >= 0) {
              cursor = transEnd + 1;
              const payload = data.subarray(cursor);
              text = compressed ? inflateCapped(payload) : utf8(payload);
            }
          }
        }
        chunks.push({ type, keyword, text });
      }
    }
    if (type === 'IEND') break;
    offset = end + 4;
  }
  return chunks;
}

export function extractKppXml(bytes) {
  const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || new ArrayBuffer(0));
  if (!isPngBytes(data)) {
    const text = utf8(data.subarray(0, Math.min(data.length, MAX_PRESET_XML_BYTES)));
    return text.includes('<Preset') ? text.slice(text.indexOf('<Preset')) : '';
  }
  for (const chunk of readPngTextChunks(data)) {
    if (/preset/i.test(chunk.keyword) && chunk.text.includes('<Preset')) return chunk.text.slice(chunk.text.indexOf('<Preset'));
  }
  for (const chunk of readPngTextChunks(data)) {
    if (chunk.text.includes('<Preset')) return chunk.text.slice(chunk.text.indexOf('<Preset'));
  }
  return '';
}

export function decodeXmlEntities(text) {
  return String(text || '')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#(\d+);/g, (m, code) => {
      const n = Number(code);
      return n > 0 && n < 0x110000 ? String.fromCodePoint(n) : '';
    })
    .replace(/&amp;/g, '&');
}

export function parseXmlAttributes(tagText) {
  const out = {};
  String(tagText || '').replace(/([A-Za-z_:][A-Za-z0-9_:.-]*)\s*=\s*"([^"]*)"/g, (m, key, value) => {
    if (key !== '__proto__' && key !== 'constructor' && key !== 'prototype') out[key] = decodeXmlEntities(value);
    return m;
  });
  return out;
}

// Returns { name, paintopid, embeddedResources, params: { name: { type, value } } }.
export function parseKritaPresetXml(xmlText) {
  const xml = String(xmlText || '');
  const presetTag = (xml.match(/<Preset\b[^>]*>/i) || [''])[0];
  const presetAttributes = parseXmlAttributes(presetTag);
  const params = {};
  const order = [];
  const regex = /<param\b([^>]*)>([\s\S]*?)<\/param>/gi;
  let match;
  while ((match = regex.exec(xml))) {
    const attributes = parseXmlAttributes(match[1]);
    const name = attributes.name;
    if (!name || name === '__proto__' || name === 'constructor' || name === 'prototype') continue;
    let body = match[2].trim();
    const cdata = body.match(/^<!\[CDATA\[([\s\S]*?)\]\]>$/);
    body = cdata ? cdata[1] : decodeXmlEntities(body);
    if (!(name in params)) order.push(name);
    params[name] = { type: attributes.type || 'string', value: body };
  }
  const embeddedResources = [];
  const resourceRegex = /<resource\b([^>]*)>\s*(?:<!\[CDATA\[)?([A-Za-z0-9+/=\s]*?)(?:\]\]>)?\s*<\/resource>/gi;
  while ((match = resourceRegex.exec(xml))) {
    const attributes = parseXmlAttributes(match[1]);
    embeddedResources.push({
      type: String(attributes.type || '').toLowerCase(),
      filename: attributes.filename || attributes.name || '',
      md5sum: attributes.md5sum || '',
      base64: match[2] || '',
    });
  }
  return {
    name: presetAttributes.name || '',
    paintopid: String(presetAttributes.paintopid || '').toLowerCase(),
    presetAttributes,
    params,
    order,
    embeddedResources,
  };
}

export function kritaParamNumber(preset, name, fallback = NaN) {
  const entry = preset && preset.params ? preset.params[name] : null;
  if (!entry) return fallback;
  const value = Number(String(entry.value).trim());
  return Number.isFinite(value) ? value : fallback;
}

export function kritaParamBool(preset, name, fallback = false) {
  const entry = preset && preset.params ? preset.params[name] : null;
  if (!entry) return fallback;
  const text = String(entry.value).trim().toLowerCase();
  if (text === 'true' || text === '1') return true;
  if (text === 'false' || text === '0') return false;
  return fallback;
}

export function kritaParamString(preset, name, fallback = '') {
  const entry = preset && preset.params ? preset.params[name] : null;
  return entry ? String(entry.value) : fallback;
}

// Krita resource bundles list their contents in META-INF/manifest.xml.
export function parseKritaBundleManifest(xmlText) {
  const entries = [];
  const regex = /<manifest:file-entry\b([^>]*?)(\/>|>([\s\S]*?)<\/manifest:file-entry>)/gi;
  let match;
  while ((match = regex.exec(String(xmlText || '')))) {
    const attributes = parseXmlAttributes(match[1]);
    const tags = [];
    String(match[3] || '').replace(/<manifest:tag>([\s\S]*?)<\/manifest:tag>/gi, (m, tag) => {
      tags.push(decodeXmlEntities(tag.trim()));
      return m;
    });
    entries.push({
      path: attributes['manifest:full-path'] || '',
      mediaType: attributes['manifest:media-type'] || '',
      md5sum: attributes['manifest:md5sum'] || '',
      tags,
    });
  }
  return entries;
}

export function parseKritaBundleMeta(xmlText) {
  const text = String(xmlText || '');
  const pick = (tag) => {
    const match = text.match(new RegExp('<(?:meta:|dc:)?' + tag + '\\b[^>]*>([\\s\\S]*?)</(?:meta:|dc:)?' + tag + '>', 'i'));
    return match ? decodeXmlEntities(match[1].trim()) : '';
  };
  return {
    name: pick('title') || pick('name'),
    author: pick('author') || pick('creator'),
    license: pick('license'),
    description: pick('description'),
    version: pick('bundle-version') || pick('version'),
  };
}
